import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import {
  ChartingLibraryWidgetOptions,
  HistoryCallback,
  IBasicDataFeed,
  IOrderLineAdapter,
  IPositionLineAdapter,
  LanguageCode,
  LibrarySymbolInfo,
  PeriodParams,
  ResolutionString,
  ResolveCallback,
  SubscribeBarsCallback,
  ThemeName,
  Timezone,
  widget,
} from '../lib/tradingview/charting_library'

import { formatChartPrice, convIntervalStr, convIntervalToResolution } from '@/shared/utils/formatter'
import { calculateDivisions, countTrailingZeros } from '@/shared/utils/maths'
import FutuCandleWSManager from '../lib/futuCandleManager'

// 선물 선택
interface tvFutuOption {
  symbol: string
  interval: string
  locale: string
  ChartKey: string
  MaxCnt: number
  pricescale: number
  timeZone: Timezone
  ready: boolean
}

interface TradingviewChartProps {
  Info: tvFutuOption
  onChangeCandleINT: (intva: string) => void
  // ▼ 추가: 주문 수정/취소 시 실행될 부모 함수
  // onModifyOrder는 성공시 true, 실패시 false 반환 (라인 복원용)
  onModifyOrder?: (orderId: number, newPrice: number) => Promise<boolean>
  onCancelOrder?: (orderId: number) => void
}

const lastBarsCache = new Map()
const supported_resolutions = ['1', '3', '5', '15', '30', '60', '240', 'D', 'W', 'M']
const favorites_supported_resolutions = ['1', '5', '30', '1h', 'D', 'W', 'M']

// 컬러옵션
const optionColor = {
  barup: '#38C08A',
  bardown: '#F23645',
  volup: '#38C08A',
  voldown: '#F23645',
  separatorColor: '#4C4F59', //거래량 이동평균선 색상
  buyopenorder: '#26A69A',
  sellopenorder: '#EF5350',
}

const TradingviewFutuChart = forwardRef<tvFutuComponentRef, TradingviewChartProps>((props, ref) => {
  // ✅ 모든 Hook을 먼저 호출
  const { symbol, interval, locale, ChartKey, MaxCnt, pricescale, timeZone } = props.Info || {}
  const optionRef = useRef({
    symbol: symbol,
    interval: interval || '60', // 기본값을 1시간으로 설정
    locale: locale,
    ChartKey: ChartKey,
    MaxCnt: MaxCnt,
    pricescale: pricescale,
    timeZone: timeZone,
    ready: false,
  })

  const positionInfo = useRef<CandleFutuPositionInfo[] | null>(null)
  const PositionLongLine = useRef<IPositionLineAdapter | null>(null) // 롱
  const PositionShortLine = useRef<IPositionLineAdapter | null>(null) // 숏
  const PositionLongClearLine = useRef<IPositionLineAdapter | null>(null) // 롱 손절
  const PositionShortCelarLine = useRef<IPositionLineAdapter | null>(null) // 롱 익절

  const orderLine = useRef<IOrderLineAdapter[]>([])
  const openorderInfo = useRef<CandleOpenOrder[] | undefined>(undefined)
  const chartContainerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLInputElement>
  const chartWidgetRef = useRef<any>(null) // 차트를 그리는 대부분 작업처리

  const lastDataReceivedTimeRef = useRef(Date.now()) // 🔹 항상 최신 값 유지
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const chartReadyRef = useRef(false) // 🔹 차트 준비 상태를 추적하는 ref 추가
  const autoSaveSubscribedRef = useRef(false) // 🔹 중복 구독 방지
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null) // 🔹 주기적 저장

  // 🔹 차트가 완전히 준비되었는지 안전하게 체크하는 함수
  const isChartReady = (): boolean => {
    return (
      chartWidgetRef.current &&
      chartWidgetRef.current._ready === true &&
      chartReadyRef.current &&
      chartWidgetRef.current.activeChart
    )
  }

  // =========================
  // 작도 포함해서 저장/복구
  // =========================
  const normalizeSavedState = (state: any) => {
    if (!state?.charts) return state

    // 저장된 심볼 interval 불러옴
    const currentSymbol = optionRef.current.symbol
    const currentInterval = optionRef.current.interval

    // 저장된 차트 json에 지표, 드로잉, 심볼 찾아내기
    for (const chart of state.charts) {
      for (const pane of chart.panes ?? []) {
        for (const src of pane.sources ?? []) {
          if (!src?.state) continue

          //  5M,30M 이슈 내가 누르는 버튼의 값과 실제 넘어가는 데이터 값이 달라서 생긴 이슈
          src.state.interval = convIntervalToResolution(String(currentInterval))

          // 🔹 메인 차트와 인디케이터만 심볼 변경, 작도(drawing)는 제외
          // 작도는 차트 좌표에 그려지므로 심볼과 무관함
          const isMainChart = src.type === 'MainSeries'
          const isStudy = src.type === 'Study'

          if (isMainChart || isStudy) {
            src.state.symbol = currentSymbol
          }
        }
      }
    }
    return state
  }

  //사용자 선택해서 그려져야하는 것 포함 레이아웃 전체 저장
  const saveChartState = () => {
    try {
      if (!isChartReady()) return
      chartWidgetRef.current.save((obj: any) => {
        const json = JSON.stringify(obj)
        // 🔹 1. 작도는 코인별로 독립 저장
        const symbolKey = `${optionRef.current.ChartKey}_${optionRef.current.symbol}`
        localStorage.setItem(symbolKey, json)

        // 🔹 2. 인디케이터 공유를 위해 공통 키에도 저장
        localStorage.setItem(optionRef.current.ChartKey, json)
      })
    } catch (e) {
      console.warn('saveChartState error', e)
    }
  }

  // 차트 준비완료
  const InitChart = async () => {
    try {
      optionRef.current.ready = true
      await waitForChartReady()
      chartReadyRef.current = true // 🔹 차트 준비 상태를 true로 설정
      loadChartData()
      setPosition(positionInfo.current)
      if (openorderInfo.current) {
        SetOpenOrder(openorderInfo.current)
      }
      onLoadChart()

      // 🔹 10초마다 자동 저장 (작도 추가 후 즉시 페이지 이동해도 안전)
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
      }
      autoSaveIntervalRef.current = setInterval(() => {
        saveChartState()
      }, 10000)
    } catch (error) {
      console.log('InitChart : ', error)
    }
  }
  //트레이딩 선 재적용
  const reapplyTradingLines = () => {
    if (!isChartReady()) return

    // 저장된 포지션/오더 라인 재생성 - chartWidgetRef.current.save/chartWidgetRef.current.load에서 포지션/오더 라인 못가져와서 다시가져오게..
    setPosition(positionInfo.current)
    if (openorderInfo.current) {
      SetOpenOrder(openorderInfo.current)
    }
  }

  const setSymbolInfo = async (symbolName: string, interval: string, pricescale: number) => {
    // 인터벌이 비어있거나 잘못된 값인 경우 기본값 설정
    const validInterval = interval || '60'
    const vv: ResolutionString = convIntervalToResolution(validInterval) as ResolutionString
    if (chartWidgetRef.current === null) {
      return
    }

    optionRef.current.symbol = symbolName
    optionRef.current.interval = validInterval
    optionRef.current.pricescale = pricescale

    if (!isChartReady()) {
      return
    }

    chartWidgetRef.current.setSymbol(symbolName, vv, (): void => {
      // 심볼 변경 완료 후 실행
      reapplyTradingLines() // 포지션/오더라인 다시 불러와서 적용
      saveChartState() // 심볼 변경 후 즉시 저장 (새로고침 시 지표 유지)
    })
  }

  // set position 만들기
  const setPosition = (position: CandleFutuPositionInfo[] | null) => {
    try {
      positionInfo.current = position
      if (!isChartReady()) {
        return
      }

      if (position === null) {
        RemovePosition()
        return
      }

      resetPosLine()
      // console.log('position : ', position)
      positionInfo.current = position
      position.forEach((el) => {
        if (el.symbol === (optionRef.current.symbol as string)) {
          drawPosLine(el)
        }
      })
    } catch (e) {
      setPosition(position)
    }
  }

  // 라인 하나 그리기
  const drawPosLine = (position: CandleFutuPositionInfo) => {
    try {
      if (!isChartReady()) return
      if (!position?.price || !position?.closepos || !position?.amt || !position?.posSide) return

      const activeChart = chartWidgetRef.current?.activeChart?.()
      if (!activeChart) return

      const isLong = position.posSide === 'LONG'
      const mainColor = isLong ? '#26A69A' : '#EF5350'

      // 현재 포지션의 Ref
      const currentLineRef = isLong ? PositionLongLine : PositionShortLine
      const currentClearLineRef = isLong ? PositionLongClearLine : PositionShortCelarLine

      const posprice = formatChartPrice(Number(position.price), optionRef.current.pricescale)
      const closePrice = formatChartPrice(Number(position.closepos), optionRef.current.pricescale)
      
      // 2. 기존 라인이 있으면 값만 업데이트 (오류 방지 핵심)
      if (currentLineRef.current && currentClearLineRef.current) {
        try {
          currentLineRef.current.setPrice(posprice).setQuantity(position.amt)
          currentClearLineRef.current.setPrice(closePrice).setQuantity(position.amt)
          return // 업데이트 성공 시 종료
        } catch (e) {
          // 업데이트 실패 시(객체 소멸 등) 재생성 진행
        }
      }

      // 3. 없거나 업데이트 실패 시 새로 생성
      currentLineRef.current?.remove()
      currentClearLineRef.current?.remove()

      // 안전한 메서드 호출: createPositionLine이 존재하는지 확인
      if (typeof activeChart.createPositionLine !== 'function') {
        console.warn('activeChart.createPositionLine is not a function')
        return
      }

      let line, clearLine
      try {
        line = activeChart.createPositionLine()
        clearLine = activeChart.createPositionLine()
      } catch (createError) {
        console.warn('Failed to create position line:', createError)
        return
      }

      if (!line || !clearLine) {
        line?.remove()
        clearLine?.remove()
        return
      }

      // 속성 및 스타일 설정
      line
        .setText('')
        .setQuantity(position.amt)
        .setPrice(posprice)
        .setExtendLeft(true)
        .setLineStyle(0)
        .setLineLength(70)
        .setBodyTextColor('#000000')
        .setLineColor(mainColor)
        .setQuantityBorderColor(mainColor)
        .setQuantityBackgroundColor(mainColor)
        .setCloseTooltip('Position Close')

      clearLine
        .setText('Liquidation Price')
        .setQuantity(position.amt)
        .setPrice(closePrice)
        .setExtendLeft(false)
        .setLineStyle(1)
        .setLineLength(100)
        .setBodyTextColor('#000000')
        .setLineColor('#FF0000')
        .setQuantityBorderColor(mainColor)
        .setQuantityBackgroundColor(mainColor)

      // Ref 저장
      currentLineRef.current = line
      currentClearLineRef.current = clearLine
    } catch (e) {
      console.error('Error in drawPosLine:', e)
    }
  }

  // 라인 정리하기
  const resetPosLine = () => {
    if (PositionLongLine.current !== null) {
      PositionLongLine.current?.remove()
      PositionLongLine.current = null
    }

    if (PositionShortLine.current !== null) {
      PositionShortLine.current?.remove()
      PositionShortLine.current = null
    }

    if (PositionLongClearLine.current !== null) {
      PositionLongClearLine.current?.remove()
      PositionLongClearLine.current = null
    }

    if (PositionShortCelarLine.current !== null) {
      PositionShortCelarLine.current?.remove()
      PositionShortCelarLine.current = null
    }
  }

  // 업데이트
  const updatePosLine = (posSide: string, profit: string, profitRatio: string) => {
    if (posSide === 'LONG') {
      if (PositionLongLine.current) {
        const msg = profit + ' (' + profitRatio + '%)'
        PositionLongLine.current.setText(msg)
      }
    }

    if (posSide === 'SHORT') {
      if (PositionShortLine.current) {
        const msg = profit + ' (' + profitRatio + '%)'
        PositionShortLine.current.setText(msg)
      }
    }
  }

  const SetOpenOrder = (openorder: CandleOpenOrder[]) => {
    try {
      if (openorder === undefined) {
        return
      }

      if (!isChartReady()) {
        return
      }

      // 미체결 라인 클리어
      RemoveOpenOrder()

      openorderInfo.current = openorder
      if (!optionRef.current.ready) {
        return
      }

      const newOrderLines: IOrderLineAdapter[] = openorder
        .map((el) => {
          if (el.symbol === optionRef.current.symbol) {
            let tmpColorText = ''
            if (el.posSide === 'LONG') {
              tmpColorText = optionColor.buyopenorder
            }

            if (el.posSide === 'SHORT') {
              tmpColorText = optionColor.sellopenorder
            }

            let { quantity } = el
            if (el.orderType === 'STOP_MARKET' || el.orderType === 'TAKE_PROFIT_MARKET') {
              quantity = ''
            }

            // LIMIT 주문만 드래그 가능, TP/SL은 드래그 불가
            const isEditable = el.orderType === 'LIMIT'
            
            const line = chartWidgetRef.current
              .activeChart()
              .createOrderLine()
              .setText(el.orderType)
              .setQuantity(quantity)
              .setPrice(el.price)
              .setExtendLeft(true)
              .setLineLength(45)
              .setBodyTextColor('#000000')
              .setLineColor(tmpColorText)
              .setQuantityBorderColor(tmpColorText)
              .setQuantityBackgroundColor(tmpColorText)
              .setEditable(isEditable) // ★ LIMIT 주문만 드래그 가능

              // LIMIT 주문일 때만 onMove 핸들러 등록
              if (isEditable) {
                // 원래 가격 저장 (실패시 복원용)
                const originalPrice = Number(el.price);
                
                line.onMove(async function (this: IOrderLineAdapter) {
                  // this.getPrice()는 이동한 위치의 가격을 가져옵니다.
                  const newPrice = this.getPrice();
                  console.log('주문 정보 el : ', el);

                  // Props로 받은 부모 함수 실행 (API 호출)
                  if (props.onModifyOrder) {
                    const success = await props.onModifyOrder(el.orderid, newPrice);
                    
                    // API 실패시 원래 가격으로 복원
                    if (!success) {
                      this.setPrice(originalPrice);
                    }
                  }
                });
              }

            // 2. X 버튼 눌렀을 때 (주문 취소)
            line.onCancel(function () {
                console.log(`Order Cancelled: ID=${el.orderid}`);
                
                // Props로 받은 부모 함수 실행
                if (props.onCancelOrder) {
                  props.onCancelOrder(el.orderid);
                }
                // UI에서 즉시 삭제하지 말고, 소켓/API 응답 후 다시 그리는 것을 권장하지만
                // UX를 위해 우선 지우려면: this.remove(); 
            });


            return line

          }
          return null
        })
        .filter(Boolean) as IOrderLineAdapter[]

      orderLine.current = newOrderLines
    } catch (e) {
      console.log(e)
    }
  }

  // 포지션 삭제
  const RemovePosition = () => {
    if (PositionLongLine.current === null && PositionShortLine.current === null) {
      return
    }

    if (PositionLongLine.current !== null) {
      PositionLongLine.current.remove()
    }

    if (PositionShortLine.current !== null) {
      PositionShortLine.current.remove()
    }

    positionInfo.current = null
  }

  // 미체결 삭제
  const RemoveOpenOrder = () => {
    if (orderLine.current === undefined) {
      return
    }
    if (orderLine.current.length === 0) {
      return
    }
    orderLine.current.forEach((el) => {
      el.remove()
    })

    orderLine.current = []
    openorderInfo.current = []
  }

  const waitForChartReady = async () => {
    return new Promise<void>((resolve) => {
      chartWidgetRef.current.onChartReady(() => {
        // 🔹 _ready 속성을 직접 true로 설정
        chartWidgetRef.current._ready = true
        resolve()
      })
    })
  }

  // 차트 데이터 로드
  const loadChartData = () => {
    try {
      // 🔹 1. 먼저 코인별 작도 데이터 시도
      const symbolKey = `${optionRef.current.ChartKey}_${optionRef.current.symbol}`
      let raw = localStorage.getItem(symbolKey)

      // 🔹 2. 없으면 공통 키에서 로드 (인디케이터 공유)
      if (!raw) {
        raw = localStorage.getItem(optionRef.current.ChartKey)
      }

      if (!raw) {
        setDefaultChart()
        return
      }

      const parsed = JSON.parse(raw)
      const normalized = normalizeSavedState(parsed)

      // 전체 레이아웃(작도 포함) 로드
      chartWidgetRef.current.load(normalized)

      // 내부 비동기 반영 직후 한 틱 뒤 재적용(이벤트 못 잡았을 대비)
      setTimeout(reapplyTradingLines, 0)
    } catch (error) {
      console.log('error : ', error)
      setDefaultChart()
    }
  }

  const setDefaultChart = () => {
    if (chartWidgetRef.current && chartWidgetRef.current.activeChart) {
      const chart = chartWidgetRef.current.activeChart()

      chart.createStudy(
        'Moving Average',
        false,
        false,
        { length: 9, source: 'close' },
        {
          'plot.color.0': '#2196f3', // 파란색으로 설정
          'plot.linewidth.0': 1, // 선의 두께 설정
        },
      )

      chart.createStudy(
        'Moving Average',
        false,
        false,
        { length: 22, source: 'close' },
        {
          'plot.color.0': '#26c6da', // 청록색으로 설정
          'plot.linewidth.0': 1, // 선의 두께 설정
        },
      )

      chart.createStudy(
        'Moving Average',
        false,
        false,
        { length: 99, source: 'close' },
        {
          'plot.color.0': '#fbc02d', // 노란색으로 설정
          'plot.linewidth.0': 1, // 선의 두께 설정
        },
      )
    }
  }

  // 지표 관련 처리
  const onChartSymbolReady = () => {
    // 🔹 중복 구독 방지
    if (autoSaveSubscribedRef.current) {
      return
    }

    autoSaveSubscribedRef.current = true

    //자동저장 트리거: onChartSymbolReady()의 onAutoSaveNeeded에서 변경될 때마다 저장
    chartWidgetRef.current.subscribe('onAutoSaveNeeded', () => {
      // 작도/레이아웃 전체 저장
      saveChartState()

      const allStudies = chartWidgetRef.current.chart().getAllStudies()
      const indicators = Object.keys(allStudies).map((studyId) => ({
        id: studyId,
        name: allStudies[studyId].name,
        inputs: allStudies[studyId].inputs,
        styles: allStudies[studyId].styles,
      }))

      localStorage.setItem('futuIndicators', JSON.stringify(indicators))
    })
  }

  const onLoadChart = () => {
    // 처음 등록할때만 필요한데
    const tmp = setInterval(() => {
      try {
        if (chartWidgetRef?.current?.activeChart()?._activateChart?.name !== null) {
          setPosition(positionInfo.current)
          if (openorderInfo.current) {
            SetOpenOrder(openorderInfo.current)
          }
          clearInterval(tmp)
        }
      } catch (e) {
        clearInterval(tmp)
      }
    }, 1000)
  }

  //==> 외부 명령 처리 함수

  //차트 분봉 변경 이벤트
  const ChangeCandleINT = (intva: string): void => {
    props.onChangeCandleINT(intva)
  }

  // 부모가 자식의 func 을 실행할때 사용하는 것
  useImperativeHandle(ref, () => ({
    SetChangeSymbol: setSymbolInfo,
    SetPosition: setPosition,
    UpdatePosition: updatePosLine,
    SetOpenOrder: SetOpenOrder,
    RemovePostion: RemovePosition,
    RemoveOpenOrder: RemoveOpenOrder,
    isReady: isChartReady, // 🔹 차트 준비 상태 확인 메서드 추가
    // MoveOrderLimit: MoveOrder_Limit,
    // CancelOrder: CancelOrder,
    // ChartCandleInt: ChangeCandleINT,
  }))

  useEffect(() => {
    if (positionInfo.current !== null) {
      if (!isChartReady()) {
        return
      }
      setPosition(positionInfo.current)
    }
  }, [positionInfo])

  // 🔹 페이지 이동/새로고침 시 저장 보장
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveChartState()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveChartState()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    // 기존 위젯 제거(중복 방지)
    if (chartContainerRef.current) {
      if (chartWidgetRef.current) {
        try {
          chartWidgetRef.current.remove()
        } catch (e) {
          console.warn('Chart remove error:', e)
        }
        chartWidgetRef.current = null
        chartReadyRef.current = false // 🔹 차트 준비 상태 초기화
      }

      // 🔹 차트 재생성 시 포지션 라인 Ref들도 초기화 (오류 방지 핵심)
      PositionLongLine.current = null
      PositionLongClearLine.current = null
      PositionShortLine.current = null
      PositionShortCelarLine.current = null

      const datefeed: IBasicDataFeed = {
        onReady: (cb: any) => {
          setTimeout(
            () =>
              cb({
                supports_search: true,
                supports_group_request: false,
                supported_resolutions: supported_resolutions,
              }),
            0,
          )
        },
        searchSymbols: (
          userInput: string,
          exchange: string,
          symbolType: string,
          onResult: (symbols: any[]) => void,
        ) => {
          // 심볼 검색 로직 구현
          const symbols = [
            {
              symbol: userInput,
              full_name: userInput,
              description: `${userInput} description`,
              exchange: exchange,
              ticker: userInput,
              type: symbolType,
            },
          ]
          onResult(symbols)
        },
        resolveSymbol: (symbolName: string, onResolve: ResolveCallback) => {
          const currentOption = optionRef.current
          const symbol_stub: LibrarySymbolInfo = {
            name: symbolName,
            ticker: symbolName,
            session: '24x7',
            timezone: 'Etc/UTC',
            // minmov: Number(convIntervalToResolution(currentOption.interval)),
            minmov: 1,
            pricescale: currentOption.pricescale,
            has_intraday: true,
            has_weekly_and_monthly: false,
            supported_resolutions: supported_resolutions as ResolutionString[],
            description: `${symbolName}`,
            type: 'crypto',
            exchange: 'binance',
            listed_exchange: 'binance',
            format: 'volume',
            volume_precision: 2,
          }

          setTimeout(() => {
            onResolve(symbol_stub)
          }, 100)
        },

        getBars: async (
          symbolInfo: LibrarySymbolInfo,
          resolution: ResolutionString,
          periodParams: PeriodParams,
          onResult: HistoryCallback,
          onError: (reason: string) => void,
        ) => {
          try {
            const { from, to, firstDataRequest, countBack } = periodParams
            const bars: Bars[] = []
            const cnt: number = calculateDivisions(countBack, optionRef.current.MaxCnt)
            // let last: number = optionRef.current.MaxCnt
            const intva = convIntervalStr(resolution)
            // console.log('getBars: async ', intva)

            let formTime = from * 1000
            let toTime = to * 1000

            if (periodParams.countBack > optionRef.current.MaxCnt) {
              for (let i = 0; i < cnt; i++) {
                const param = `?symbol=${symbolInfo.name}&interval=${intva}&startTime=${formTime}&endTime=${toTime}&limit=1500`
                const response = await makeApiRequest(param)

                if (!response) {
                  console.warn(`getBars: API 요청 실패 (시도 ${i + 1}/${cnt})`)
                  break
                }

                const { bars: tbars, ltime } = TransBarForResponse((await response?.json()) ?? [])

                if (tbars.length === 0) {
                  console.log(`getBars: 더 이상 데이터 없음 (시도 ${i + 1}/${cnt})`)
                  break
                }

                bars.push(...tbars)

                // 🔹 첫 번째 요청에서는 tbars[0].time 사용, 그 이후는 bars 전체에서 최소값
                const minTimestamp =
                  tbars.length > 0
                    ? tbars.reduce((min, bar) => (bar.time < min ? bar.time : min), tbars[0].time)
                    : toTime * 1000

                toTime = minTimestamp / 1000

                // 🔹 다음 구간: 최소 타임스탬프 직전까지
                if (ltime >= toTime * 1000) {
                  break
                }

                formTime = from * 1000
                toTime = minTimestamp / 1000 - 1 // 1ms 전까지 (중복 방지)

                if (toTime <= 0) {
                  break
                }
              }
            } else {
              const param = `?symbol=${symbolInfo.name}&interval=${intva}&startTime=${formTime}&endTime=${toTime}&limit=1500`
              const response = await makeApiRequest(param)
              const retim = await response?.json()
              const { bars: tbars } = TransBarForResponse(retim)
              bars.push(...tbars)
            }

            if (bars.length === 0) {
              // 데이터가 없을 경우 noData 플래그를 true로 설정하여 호출
              onResult([], { noData: true })
              return
            }

            if (firstDataRequest) {
              lastBarsCache.set(symbolInfo.name, {
                ...bars[bars.length - 1],
              })
            }

            onResult(bars, { noData: false })
          } catch (error: any) {
            console.error('getBars 에러:', error)
            // 🔹 에러 발생 시에도 빈 데이터 반환 (차트 끊김 방지)
            onResult([], { noData: true })
          }
        },
        subscribeBars: (symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, onTick: SubscribeBarsCallback) => {
          const wsManager = FutuCandleWSManager.getInstance()
          const intva = convIntervalStr(resolution)
          const key = `${symbolInfo.ticker}_${intva}`
          // 현재 보고 있는 코인을 저장
          optionRef.current.symbol = symbolInfo.ticker ?? ''
          optionRef.current.interval = intva
          // // 기존 소켓 제거 후 새로운 소켓 추가
          wsManager.removeSocket(symbolInfo.ticker ?? '', intva)
          const socket = wsManager.addSocket(symbolInfo.ticker ?? '', intva)

          // 데이터 수신 감지 및 재연결 로직
          const checkConnection = () => {
            const now = Date.now()
            // 🔹 현재 보고 있는 코인만 감지 (|| 수정: 다른 코인이면 체크 안 함)
            if (optionRef.current.symbol !== symbolInfo.ticker || optionRef.current.interval !== intva) {
              return
            }

            // 🔹 15초 동안 데이터가 없으면 재연결 (60초 → 15초로 단축)
            if (now - lastDataReceivedTimeRef.current > 15000) {
              console.warn(
                `[${key}] 데이터 수신 없음 (${Math.floor((now - lastDataReceivedTimeRef.current) / 1000)}초). 웹소켓 재연결 시도.`,
              )
              wsManager.removeSocket(symbolInfo.ticker ?? '', intva) // 기존 소켓 정리
              const newSocket = wsManager.addSocket(symbolInfo.ticker ?? '', intva) // 새로운 소켓 추가
              lastDataReceivedTimeRef.current = Date.now() // 재연결 시도 시간 업데이트

              newSocket.SetCallbackFunc((data) => {
                const parsedData = data
                if (!parsedData?.k?.t) return
                lastDataReceivedTimeRef.current = Date.now() // 데이터 수신 시간 갱신
                const newBar: Bars = {
                  time: parsedData.k.t,
                  open: Number(parsedData.k.o),
                  high: Number(parsedData.k.h),
                  low: Number(parsedData.k.l),
                  close: Number(parsedData.k.c),
                  volume: Number(parsedData.k.v),
                }
                onTick(newBar)
              })
            }
          }
          // 🔹 5초마다 체크 (60초 → 5초로 단축, 빠른 감지)
          intervalRef.current = setInterval(checkConnection, 5000)

          socket.SetCallbackFunc((data: KlineEvent) => {
            const parsedData = data
            if (!parsedData?.k?.t) return
            lastDataReceivedTimeRef.current = Date.now() // 데이터 수신 시간 갱신
            if (symbolInfo.ticker === optionRef.current.symbol && optionRef.current.interval === intva) {
              const newBar: Bars = {
                time: parsedData.k.t,
                open: Number(parsedData.k.o),
                high: Number(parsedData.k.h),
                low: Number(parsedData.k.l),
                close: Number(parsedData.k.c),
                volume: Number(parsedData.k.v),
              }
              onTick(newBar)
            }
          })
          ChangeCandleINT(intva)
          onChartSymbolReady()
        },
        unsubscribeBars: (subscriberUID: string) => {
          const wsManager = FutuCandleWSManager.getInstance()
          const parts = subscriberUID.split('_')

          const symbol = parts[0] // "BTCUSDT"
          const interval = parts[2] // "60"
          if (symbol && interval) {
            wsManager.removeSocket(symbol, convIntervalStr(interval))
          }
        },
      }

      const widgetOption: ChartingLibraryWidgetOptions = {
        symbol: optionRef.current.symbol,
        interval: optionRef.current.interval as ResolutionString,
        container: chartContainerRef.current,
        datafeed: datefeed,
        // /v2 프리픽스 환경 대응: 절대경로로 지정하여 정적 파일 로드 보장
        library_path: '/v2/static/charting_library/',
        locale: optionRef.current.locale as LanguageCode,
        timezone: optionRef.current.timeZone,
        disabled_features: [
          'header_symbol_search',
          'border_around_the_chart',
          'symbol_search_hot_key',
          'header_compare',
          'volume_force_overlay',
          'popup_hints',
        ],
        enabled_features: [
          'side_toolbar_in_fullscreen_mode',
          'hide_left_toolbar_by_default',
          'iframe_loading_compatibility_mode',
        ],
        favorites: {
          intervals: favorites_supported_resolutions as ResolutionString[] | undefined,
        },
        charts_storage_url: 'https://saveload.tradingview.com',
        charts_storage_api_version: '1.1',
        client_id: 'tradingview.com',
        user_id: 'public_user_id',
        fullscreen: false,
        autosize: true,
        theme: 'dark' as ThemeName,
        // custom_css_url: '/static/tradingview/themed.css',
        overrides: {
          'mainSeriesProperties.minTick': `${optionRef.current.pricescale},1,false`,
          'mainSeriesProperties.candleStyle.upColor': optionColor.barup,
          'mainSeriesProperties.candleStyle.downColor': optionColor.bardown,
          'mainSeriesProperties.candleStyle.borderUpColor': optionColor.barup,
          'mainSeriesProperties.candleStyle.borderDownColor': optionColor.bardown,
          'mainSeriesProperties.candleStyle.wickUpColor': optionColor.barup,
          'mainSeriesProperties.candleStyle.wickDownColor': optionColor.bardown,
          'priceScale.mode': 0,
          'priceScale.autoScale': true,
          'priceScale.invertScale': true,
          // 배경색 설정 안바뀜...
          // 'paneProperties.background': '#1B1C1D', // 차트 배경색
          // 'paneProperties.vertGridProperties.color': '#32363D', // 세로 그리드 색상
          // 'paneProperties.horzGridProperties.color': '#32363D', // 가로 그리드 색상
          // 'scalesProperties.textColor': '#EFE9E9', // 가격 스케일 텍스트 색상
          // 'scalesProperties.backgroundColor': '#1B1C1D', // 가격 스케일 배경색
        },
        custom_formatters: {
          priceFormatterFactory: () => {
            return {
              format: (price) => Number(price).toFixed(countTrailingZeros(optionRef.current.pricescale)),
            }
          },
        },
      }

      const tvWidget = new widget(widgetOption)
      chartWidgetRef.current = tvWidget
      chartWidgetRef.current.onChartReady(() => {
        // 🔹 _ready 속성을 직접 true로 설정
        chartWidgetRef.current._ready = true

        // 준비 이벤트 설정
        chartWidgetRef.current.applyOverrides({
          'paneProperties.separatorColor': optionColor.separatorColor, // separatorColor 적용
          'mainSeriesProperties.candleStyle.upColor': optionColor.barup,
          'mainSeriesProperties.candleStyle.downColor': optionColor.bardown,
          'mainSeriesProperties.candleStyle.borderUpColor': optionColor.barup,
          'mainSeriesProperties.candleStyle.borderDownColor': optionColor.bardown,
          'mainSeriesProperties.candleStyle.wickUpColor': optionColor.barup,
          'mainSeriesProperties.candleStyle.wickDownColor': optionColor.bardown,
          'mainSeriesProperties.minTick': `${pricescale},1,false`,
        })

        const chart = chartWidgetRef.current.activeChart()

        //onDataLoaded / onSymbolChanged / onIntervalChanged  reapplyTradingLines() 호출 타이밍 이슈 방지
        chart.onDataLoaded().subscribe(null, () => {
          reapplyTradingLines()
        })

        chart.onSymbolChanged().subscribe(null, () => {
          reapplyTradingLines()
        })

        chart.onIntervalChanged().subscribe(null, () => {
          reapplyTradingLines()
        })

        InitChart()
      })
    }

    return () => {
      console.log('인터벌 해제 및 소켓 정리')

      // 🔹 자동 저장 인터벌 정리
      if (autoSaveIntervalRef.current !== null) {
        clearInterval(autoSaveIntervalRef.current)
        autoSaveIntervalRef.current = null
      }

      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      saveChartState()
      chartWidgetRef.current?.remove?.()

      // 🔹 구독 상태 리셋
      autoSaveSubscribedRef.current = false

      const wsManager = FutuCandleWSManager.getInstance()
      wsManager.clearAllSockets()
    }
  }, [])

  return (
    <div className='h-full'>
      {/* {isLoading && <div className={loadingSpinner}>Loading...</div>} */}
      <div ref={chartContainerRef} style={{ height: '100%' }} />
    </div>
  )
})

async function makeApiRequest(path: string): Promise<Response | undefined> {
  try {
    const url = `${process.env.NEXT_PUBLIC_FUTU_URL}/fapi/v1/klines${path}`
    const response = await fetch(url)
    return response
  } catch (error) {
    console.log('Chart Api Error', error)
  }
}

// bar만들어주는 transfer 작업
function TransBarForResponse(response: Response) {
  const bars: Bars[] = []
  let ltime: number = 0
  if (Array.isArray(response)) {
    // 바이낸스 spot
    for (let i = 0; i < response.length; i++) {
      ltime = response[i][0]
      const bar: Bars = {
        time: response[i][0],
        low: Number(response[i][3]),
        high: Number(response[i][2]),
        open: Number(response[i][1]),
        close: Number(response[i][4]),
        volume: Number(response[i][5]),
      }
      bars[i] = bar
    }
  }
  return { bars, ltime }
}

export default TradingviewFutuChart
